/**
 * Merges a user's saved hidden-columns preference with columns added to the
 * app since that preference was written.
 *
 * A saved `hiddenColumns` cookie replaces the defaults wholesale, so a column
 * introduced after the cookie was written would come up visible even when it
 * is hidden by default — the saved array cannot distinguish "user showed this
 * column" from "this column did not exist yet". `knownColumns` resolves that:
 * any default-hidden column the user's config has never seen is forced hidden;
 * columns the user knowingly unhid stay visible.
 *
 * The caller owns deciding which column set a given saved preference had seen
 * (the resources list maps its persisted schema version to one), so
 * `knownColumns` is always a concrete list here — a cookie too old to record a
 * version resolves to the earliest known set rather than to null.
 */
export function reconcileHiddenColumns(
  savedHiddenColumns: string[],
  knownColumns: string[],
  options: {
    /** Every valid column key (stale saved keys are dropped). */
    allColumns: string[];
    /** Columns hidden by default for new users. */
    defaultHidden: string[];
    /** Columns that may be hidden at all (e.g. `fullName` may not). */
    isHideable: (column: string) => boolean;
  },
): string[] {
  const restored = savedHiddenColumns.filter(
    (column) =>
      options.allColumns.includes(column) && options.isHideable(column),
  );

  const unseenHiddenDefaults = options.defaultHidden.filter(
    (column) => !knownColumns.includes(column),
  );

  return [...new Set([...restored, ...unseenHiddenDefaults])];
}
